/* eslint-disable no-unused-vars */
import { addTransportItemsCommand, addTransportItemsCondition, setTransportItemsActivity } from 'app/actions/map';
import { addEventQueue } from 'app/actions/notification';
import { setWebsocketConnect, setWebsocketStatus } from 'app/actions/websocket';
import { transportConditionLoad } from 'app/store/map';
import { batch } from 'react-redux';

// ПО промежуточного слоя использует действия для взаимодействия с соединением WebSocket,
// включая подключение, отключение, отправку и получение сообщений.
export const websocketMiddleware = function () {
    let websocket = null,
        hasOpened = false,
        connectedPayload = null,
        reconnectionInterval = null,
        messageQueue = [],
        messageInterval = null,
        visibilityHandler = null,
        hiddenQueue = [], // Очередь для критичных сообщений при неактивной вкладке
        isProcessingHiddenQueue = false, // Флаг обработки накопленной очереди
        hiddenSince = null; // Время когда вкладка стала неактивной

    // Максимальный размер очереди сообщений для предотвращения переполнения памяти.
    const MAX_QUEUE_SIZE = 500;
    // Максимальный размер очереди для неактивной вкладки (только критичные сообщения).
    // Рассчитано: при частоте 1 событие/сек за 10 минут = 600 событий, берем 500 с запасом.
    const MAX_HIDDEN_QUEUE_SIZE = 500;
    // Размер порции для обработки накопленных сообщений при возврате на вкладку.
    // Увеличено для более эффективной обработки (современные браузеры справляются).
    const BATCH_SIZE = 50;
    // Порог для перезапроса: если очередь больше этого значения, делаем перезапрос из БД.
    // Увеличено, чтобы чаще использовать порционную обработку вместо перезапроса.
    const RELOAD_THRESHOLD = 200;
    // Порог времени неактивности в миллисекундах (10 минут) для перезапроса.
    // Увеличено для более разумного порога перезапроса.
    const INACTIVE_TIME_THRESHOLD = 10 * 60 * 1000;

    // Сохранить ссылку на store для доступа к состоянию.
    let store = null;

    // Обработчик событий подключения через WebSocket.
    const connectEvent = (dispatch) => {
        // Проверить, не открыто ли уже соединение.
        if (websocket && (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING)) {
            return; // Соединение уже открыто или открывается, не создаем новое.
        }

        // Закрыть старое соединение, если оно есть в другом состоянии.
        if (websocket) {
            closeOpenConnection(1000, 'Reconnecting');
        }

        // Очистить старый интервал обработки сообщений, если он есть.
        if (messageInterval) {
            clearInterval(messageInterval);
            messageInterval = null;
            messageQueue = [];
            hiddenQueue = [];
            isProcessingHiddenQueue = false;
            hiddenSince = null;
        }

        // Удалить старый обработчик видимости вкладки, если он есть.
        if (visibilityHandler) {
            document.removeEventListener('visibilitychange', visibilityHandler);
            visibilityHandler = null;
        }

        // Сохранить последнюю полезную нагрузку подключения,
        // чтобы, при автоматическом переподключении повторно использовать ее.
        // eslint-disable-next-line no-useless-escape
        connectedPayload = location.origin.replace(/(http)(s)?\:\/\//, 'ws$2://') + '/ws';

        // Установить соединение с сервером по технологии WebSocket.
        websocket = new WebSocket(connectedPayload);

        // Обработка открытия соединения
        websocket.addEventListener('open', (event) => {
            // Очистить все невыполненные попытки повторного подключения.
            if (reconnectionInterval) {
                clearInterval(reconnectionInterval);
                reconnectionInterval = null;
            }

            // Изменить статус WebSocket подключения.
            dispatch(setWebsocketConnect(event));

            // Отследить, что мы смогли открыть соединение. Мы можем использовать этот флаг
            // для обработки ошибок позже, гарантируя, что мы не попытаемся переподключиться, когда
            // соединение вообще никогда не открывалось.
            hasOpened = true;
        });

        // Обработка закрытия соединения.
        websocket.addEventListener('close', (event) => {
            // Очистить все невыполненные попытки такта цикла событий.
            if (messageInterval) {
                clearInterval(messageInterval);
                messageInterval = null;
            }

            // Очистить очереди сообщений при закрытии соединения.
            messageQueue = [];
            hiddenQueue = [];
            isProcessingHiddenQueue = false;
            hiddenSince = null;

            // Условно пытаться переподключиться, если включено и применимо.
            if (canAttemptReconnect()) {
                restoreBrokenConnection(dispatch);
            }
        });

        // Обработка события сообщения.
        websocket.addEventListener('message', (event) => {
            try {
                // Получить тело сообщения из закодированной строки.
                const { type, data } = JSON.parse(event.data);

                // Если вкладка неактивна, сохранять только критичные сообщения.
                if (document.visibilityState === 'hidden') {
                    // Сохранять только критичные типы сообщений (conditions и activity).
                    if (type === 'blackbox' || type === 'wialon_data' || type === 'activity') {
                        // Ограничить размер очереди неактивной вкладки.
                        if (hiddenQueue.length >= MAX_HIDDEN_QUEUE_SIZE) {
                            hiddenQueue.shift(); // Удалить самое старое сообщение.
                        }

                        // Сохранить критичное сообщение в очередь неактивной вкладки.
                        if (type === 'blackbox' || type === 'wialon_data') {
                            hiddenQueue.push(addTransportItemsCondition(data));
                            if ([1].includes(data?.event_id)) {
                                dispatch(addEventQueue(data));
                            }
                        } else if (type === 'activity') {
                            hiddenQueue.push(setTransportItemsActivity(data));
                        }
                    }
                    // Пропустить некритичные сообщения при неактивной вкладке.
                    return;
                }

                // Обработка сообщений при активной вкладке.
                switch (type) {
                    case 'blackbox':
                    case 'wialon_data':
                        // Ограничить размер очереди, удаляя самые старые сообщения при переполнении.
                        if (messageQueue.length >= MAX_QUEUE_SIZE) {
                            messageQueue.shift(); // Удалить самое старое сообщение.
                        }
                        messageQueue.push(addTransportItemsCondition(data));
                        if ([1].includes(data?.event_id)) {
                            dispatch(addEventQueue(data));
                        }
                        break;
                    case 'activity':
                        // Ограничить размер очереди, удаляя самые старые сообщения при переполнении.
                        if (messageQueue.length >= MAX_QUEUE_SIZE) {
                            messageQueue.shift(); // Удалить самое старое сообщение.
                        }
                        messageQueue.push(setTransportItemsActivity(data));
                        break;
                    case 'cmd_send':
                        dispatch(addTransportItemsCommand(data));
                        break;
                }
            } catch (e) {
                console.error(e);
            }
        });

        // Функция для порционной обработки накопленной очереди при возврате на вкладку.
        const processQueueInBatches = (queue, dispatch) => {
            if (isProcessingHiddenQueue || queue.length === 0) {
                return;
            }

            isProcessingHiddenQueue = true;

            const processBatch = () => {
                if (queue.length === 0) {
                    isProcessingHiddenQueue = false;
                    return;
                }

                // Взять порцию сообщений для обработки.
                const messageBatch = queue.splice(0, BATCH_SIZE);

                // Обработать порцию в одном батче Redux.
                batch(() => {
                    for (const message of messageBatch) {
                        dispatch(message);
                    }
                });

                // Продолжить обработку следующей порции с небольшой задержкой.
                // Используем requestAnimationFrame для более плавной обработки (≈16ms при 60fps).
                if (queue.length > 0) {
                    requestAnimationFrame(() => {
                        setTimeout(processBatch, 0);
                    });
                } else {
                    isProcessingHiddenQueue = false;
                }
            };

            // Начать обработку первой порции с минимальной задержкой.
            requestAnimationFrame(() => {
                setTimeout(processBatch, 0);
            });
        };

        // Установим интервал такта цикла событий в один проход рендеринга.
        messageInterval = setInterval(() => {
            batch(() => {
                for (const message of messageQueue) {
                    dispatch(message);
                }

                messageQueue = [];
            });
        }, 5000);

        // Обработчик изменения видимости вкладки для предотвращения накопления сообщений.
        visibilityHandler = () => {
            if (document.visibilityState === 'hidden') {
                // Запомнить время когда вкладка стала неактивной.
                hiddenSince = Date.now();
                // Очистить обычную очередь при скрытии вкладки.
                // Критичные сообщения продолжают накапливаться в hiddenQueue.
                messageQueue = [];
            } else if (document.visibilityState === 'visible') {
                // Вычислить время неактивности.
                const inactiveTime = hiddenSince ? Date.now() - hiddenSince : 0;
                const shouldReload = hiddenQueue.length >= RELOAD_THRESHOLD || inactiveTime >= INACTIVE_TIME_THRESHOLD;

                // Если очередь большая или время неактивности большое - перезапросить данные из БД.
                if (shouldReload && store) {
                    // Получить текущее состояние из store для определения выбранного транспорта.
                    const state = store.getState();
                    const selectedTransportId = state?.map?.transportSelectedItem?.id;

                    if (selectedTransportId) {
                        // Перезапросить данные из БД для выбранного транспорта.
                        store.dispatch(transportConditionLoad(selectedTransportId));
                    }

                    // Очистить очередь после перезапроса.
                    hiddenQueue = [];
                    isProcessingHiddenQueue = false;
                } else if (hiddenQueue.length > 0) {
                    // Если очередь небольшая - обработать накопленные критичные сообщения порциями.
                    processQueueInBatches(hiddenQueue, dispatch);
                }

                // Очистить обычную очередь, новые сообщения будут обрабатываться сразу.
                messageQueue = [];
                hiddenSince = null;
            }
        };

        document.addEventListener('visibilitychange', visibilityHandler);
    };

    // Обработчик события отключения WebSocket вручную.
    const closeEvent = (dispatch) => {
        // Закрыть сокетное соединение
        closeOpenConnection(4000);

        // Очистим интервал такта цикла событий.
        if (messageInterval) {
            clearInterval(messageInterval);

            messageQueue = [];
            hiddenQueue = [];
            isProcessingHiddenQueue = false;
            hiddenSince = null;
            messageInterval = null;
        }

        // Удалить обработчик видимости вкладки.
        if (visibilityHandler) {
            document.removeEventListener('visibilitychange', visibilityHandler);
            visibilityHandler = null;
        }

        // Отправка действий для уведомления Redux о закрытие соединения.
        dispatch(setWebsocketStatus('CLOSED'));
    };

    // Обработчик событий отправки сообщения WebSocket.
    const sendEvent = (dispatch, action) => {
        if (websocket) {
            websocket.send(JSON.stringify(action.payload));
        }
    };

    // Обработчик события закрытия WebSocket.
    const closeOpenConnection = (code, reason) => {
        if (websocket) {
            websocket.close(code || 1000, reason || 'WebSocket connection closed');
            websocket = null;
            hasOpened = false;
        }
    };

    // Обработчик события восстановления сломанного соединение WebSocket.
    const restoreBrokenConnection = (dispatch) => {
        websocket = null;

        // Отправка действий для уведомления Redux о ожидании соединения.
        dispatch(setWebsocketStatus('PENDING'));

        // Попытаться немедленно переподключиться, вызвав connect с утверждениями,
        // что аргументы соответствуют типам, которые мы ожидаем.
        connectEvent(dispatch);

        // Попытка повторного подключения через интервал.
        reconnectionInterval = setInterval(() => {
            // Закрыть соединение с причиной новой попытки.
            closeOpenConnection(4001);

            // Отправка действий для уведомления Redux о ожидании соединения.
            dispatch(setWebsocketStatus('PENDING'));

            // Вызвать соединение снова, таким же образом.
            connectEvent(dispatch);
        }, 12000);
    };

    // Проверка условий при повторном подключении, если соединение когда-либо было успешно открыто,
    // и в настоящее время мы не пытаемся переподключиться.
    // Это предотвращает повторяющиеся циклы переподключения к соединениям, которые не были
    // успешно открыты ранее, например ошибки net::ERR_CONNECTION_REFUSED.
    // Это также предотвращает запуск нескольких циклов попыток переподключения.
    const canAttemptReconnect = () => {
        return hasOpened && reconnectionInterval == null;
    };

    return (storeInstance) => (next) => (action) => {
        // Сохранить ссылку на store для использования в обработчиках.
        store = storeInstance;

        switch (action.type) {
            case 'WEBSOCKET_OPEN_CONNECT':
                connectEvent(store.dispatch, action);
                break;
            case 'WEBSOCKET_CLOSE_CONNECT':
                closeEvent(store.dispatch, action);
                break;
            case 'WEBSOCKET_STOP_CONNECT':
                closeEvent(store.dispatch, action);
                break;
            case 'WEBSOCKET_SEND_MESSAGE':
                sendEvent(store.dispatch, action);
                break;
            default:
                return next(action);
        }
    };
};

export default websocketMiddleware;
